import { contentsMap } from '~/features-shared/content/stores/contents-db'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import type { ITag, ITaggable } from '~/features-shared/tags/interfaces/tags'
import { processTagAttribute } from '~/features-shared/tags/utils/6wpanel'
import { getFilterDescription, getFilterPredicate } from '~/features-shared/tags/utils/user-tags'

enum ETagAction {
  ADD = 'add',
  REMOVE = 'remove',
}

interface RawTag {
  predicate: string
  object: {
    literal: string
  }
}

const OPPOSITE = (x: ETagAction) => x === ETagAction.ADD ? ETagAction.REMOVE : ETagAction.ADD

function areObjectsSame(first: string | string[], second: string | string[]) {
  // rather than comparing arrays, comparing strings is easier?
  const firstString = Array.isArray(first) ? first.join('/') : first
  const secondString = Array.isArray(second) ? second.join('/') : second

  return firstString === secondString
}

// eslint-disable-next-line max-statements
export function useTaggableContent(subjectUri: SUBJECT_6W_URI): ITaggable {
  // State
  const sixWTags = reactive<ITag[]>([])
  const tempSixWTags = reactive<ITag[]>([]) // enables to have a view of the tags temporary state during edition

  // Tags in edition
  const reformattedTaggingActions6w = reactive({
    add: <Record<string, RawTag>>{},
    remove: <Record<string, RawTag>>{},
  })
  const taggingActions6w = reactive({
    add: [{ subject: subjectUri, tag: <RawTag[]>[] }],
    remove: [{ subject: subjectUri, tag: <RawTag[]>[] }],
  })
  const tagsToSendArray = reactive({
    add: <RawTag[]>[],
    remove: <RawTag[]>[],
  })

  const content = computed(() => contentsMap.get(subjectUri))
  const subject6wUriForTagger = computed(() => content.value?.tags6w_subjecturi || subjectUri)

  const tags = computed(() => sixWTags)
  const tagsWithSubjectURIData = computed(() => {
    const obj: Record<SUBJECT_6W_URI, ITag[]> = {}
    sixWTags.filter(tag => Object.hasOwnProperty.call(tag, 'subjectURI'))
      .forEach((tag) => {
        const subject6wuri = tag.subjectURI as SUBJECT_6W_URI
        if (!(subject6wuri in obj))
          obj[subject6wuri] = []

        obj[subject6wuri].push(tag)
      })
    return reactive(obj)
  })
  const tagsWithoutSubjectURIData = computed(() => reactive(
    sixWTags.filter(tag => !Object.hasOwnProperty.call(tag, 'subjectURI')),
  ))

  /**
   * Function to be called when tags change so as to recompute tags from properties
   */
  const reformatTags = () => {
    sixWTags.length = 0
    const implicit = content.value?.tags6w_implicit || []
    const explicit = content.value?.tags6w || content.value?.tags || []
    const tags = processTagAttribute(subject6wUriForTagger.value, [...implicit, ...explicit], [], 'array') as ITag[]
    sixWTags.push(...tags)
  }

  const _pushToTagsAction = (tag: Record<string, string>, action: ETagAction) => {
    const oppositeAction = OPPOSITE(action)

    const tagId = `${tag.predicate}/${tag.object}`
    const tagObjToSend: RawTag = {
      predicate: tag.predicate || tag.sixw,
      object: {
        literal: tag.object,
      },
    }

    if (reformattedTaggingActions6w[oppositeAction][tagId]) {
      delete reformattedTaggingActions6w[oppositeAction][tagId]
      const taggingActionsArr = tagsToSendArray[oppositeAction]
      for (let i = 0, len = taggingActionsArr.length; i < len; i++) {
        if (`${taggingActionsArr[i].predicate}/${taggingActionsArr[i].object.literal}` === tagId) {
          taggingActionsArr.splice(i, 1)
          break
        }
      }
      taggingActions6w[oppositeAction][0].tag = taggingActionsArr
    }
    else if (!reformattedTaggingActions6w[action][tagId]) {
      reformattedTaggingActions6w[action][tagId] = tagObjToSend
      tagsToSendArray[action].push(tagObjToSend)
      taggingActions6w[action][0].tag = tagsToSendArray[action]
    }
  }

  /**
   * Callback to be called to add a tag to the content
   *
   * @param tagObj The tag to be added
   * @param addTagTemporarily true, if we want to add the tag to the "changes"
   */
  const addTag = (tagObj: Record<string, string>, addTagTemporarily = false) => {
    let tags: any[] | undefined
    if (addTagTemporarily) {
      // Get tags from content's changes
      tags = tempSixWTags.length ? [...tempSixWTags] : [...(content.value?.tags6w || [])]
    }
    else {
      tags = content.value?.tags6w
    }

    if (!tags)
      tags = []

    // Return if tag is already present
    const predicate = getFilterPredicate(tagObj.object, tagObj.predicate)
    const object = getFilterDescription(tagObj.object)
    if (tags.find(t => t.predicate === predicate && areObjectsSame(t.tag, object)))
      return

    // Push the new tag
    tags.push({
      predicate,
      tag: object,
    })

    if (addTagTemporarily) {
      // Set tags for update actions
      _pushToTagsAction(tagObj, ETagAction.ADD)
      tempSixWTags.length = 0
      tempSixWTags.push(...tags)
    }
    else if (content.value) {
      content.value.tags6w = tags
    }
  }

  /**
   * Callback to be called to remove a tag from the content
   *
   * @param tagObj The tag to be removed
   * @param removeTagTemporarily true, if we want to remove the tag from the "changes"
   */
  const removeTag = (tagObj: Record<string, string>, removeTagTemporarily = false) => {
    let tags: any[] | undefined
    if (removeTagTemporarily) {
      // Get tags from content's changes
      tags = tempSixWTags.length ? [...tempSixWTags] : [...(content.value?.tags6w || [])]
    }
    else {
      tags = content.value?.tags6w
    }

    if (!tags)
      tags = []

    const predicate = getFilterPredicate(tagObj.object, tagObj.predicate)
    const object = getFilterDescription(tagObj.object)
    const tagIndex = tags.findIndex(t => t.predicate === predicate && areObjectsSame(t.tag, object))
    // Return if tag is already not present
    if (tagIndex < 0)
      return

    // Remove the tag
    tags.splice(tagIndex, 1)

    if (removeTagTemporarily) {
      // Set tags for update actions
      _pushToTagsAction(tagObj, ETagAction.REMOVE)
      tempSixWTags.length = 0
      tempSixWTags.push(...tags)
    }
    else if (content.value) {
      content.value.tags6w = tags
    }
  }

  /**
   * Should reset tags edition related data
   */
  const resetTaggingSession = (leaveTempTags = false) => {
    reformattedTaggingActions6w.add = {}
    reformattedTaggingActions6w.remove = {}

    taggingActions6w.add[0].tag.length = 0
    taggingActions6w.remove[0].tag.length = 0

    tagsToSendArray.add.length = 0
    tagsToSendArray.remove.length = 0

    if (!leaveTempTags)
      tempSixWTags.length = 0
  }

  whenever(() => content, reformatTags, { immediate: true }) // Initial computation
  whenever(() => content.value?.tags6w, reformatTags)
  whenever(() => content.value?.tags, reformatTags)
  whenever(() => content.value?.tags6w_implicit, reformatTags)

  return {
    enableTagging: true,
    sixwURI: subject6wUriForTagger,
    tags,
    temporaryTags: tempSixWTags,
    tagsWithSubjectURIData,
    tagsWithoutSubjectURIData,

    addTag,
    removeTag,

    resetTaggingSession,
    taggingActions6w,
    tagsToSendArray,
  }
}
